import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, User } from "lucide-react";import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BG = "/plaza-bg.jpg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin/dashboard" : "/worker/map", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      navigate(res.user.role === "admin" ? "/admin/dashboard" : "/worker/map", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-stone-900/45 backdrop-blur-[2px]" />

      <div className="relative z-10 w-full max-w-md bb-fade-up">
        <div className="rounded-3xl border border-white/40 bg-white/95 p-8 shadow-2xl backdrop-blur-xl">
          <div className="mb-7 flex flex-col items-center text-center">
            <img src="/plaza-logo.png" alt="Plaza - Hotel & Spa" className="mb-3 h-44 w-44 object-contain" />
            <p className="mt-1 text-sm text-stone-500">Baldachin availability &amp; worker accountability</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-sm font-semibold">Username</Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <Input
                  id="username"
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. admin"
                  autoComplete="username"
                  className="h-12 rounded-xl pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-12 rounded-xl pl-10"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="h-12 w-full rounded-xl text-base font-bold"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
